import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Skull, Swords, Hourglass, ArrowRight, Share2, Check } from "lucide-react";

/**
 * Daily-style "Match Complete" lock card shown on the Play page once a
 * tournament match score has been submitted. Replaces the GameCanvas so
 * the pilot is steered into a clean post-match UX (mirrors the
 * <LockedCard /> pattern on /daily) instead of staring at the legacy
 * stop overlay over the live canvas.
 *
 * Props:
 *   - tournamentId: bracket id (used for the View-bracket CTA + the
 *     standings deep-link)
 *   - level:        the catalog level the bracket was played on (display only)
 *   - myScore:      pilot's submitted score
 *   - oppScore:     opponent's score (null if not in yet — "waiting" state)
 *   - oppName:      opponent display name (or "Opponent")
 *   - oppIsBot:     boolean — colors the opp chip cyan if true
 *   - outcome:      "win" | "lose" | "waiting" — drives theme color
 */
const THEME = {
  win:    { color: "#00ff41", label: "Match Won",     icon: Trophy,    desc: "Bracket advances on your back." },
  lose:   { color: "#ff007f", label: "Match Lost",    icon: Skull,     desc: "Eliminated from the bracket." },
  waiting:{ color: "#00ffff", label: "Score Submitted", icon: Hourglass, desc: "Waiting on your opponent…" },
};

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString());

export default function TournamentMatchCompleteCard({
  tournamentId,
  level,
  myScore,
  oppScore,
  oppName,
  oppIsBot,
  outcome,
}) {
  const theme = THEME[outcome] || THEME.waiting;
  const Icon = theme.icon;
  const tone = theme.color;
  const [copied, setCopied] = useState(false);

  // OG-rich share URL — same /api/share/replay/<id> wrapper used by the
  // TournamentDetail "Spectator URL" copy action. Discord/Twitter unfurl
  // the bracket card automatically via the server-side OG meta tags.
  const spectatorUrl =
    typeof window !== "undefined" && tournamentId
      ? `${window.location.origin}/api/share/replay/${tournamentId}`
      : "";

  const handleShare = async () => {
    if (!spectatorUrl) return;
    const verb = outcome === "win"
      ? "Just won my tournament match"
      : outcome === "lose"
        ? "Got dropped in the bracket"
        : "Submitted a tournament score";
    const oppLabel = oppName ? ` vs ${oppName}` : "";
    const text = `${verb}${oppLabel} on Cosmic Worm Breaker — ${myScore?.toLocaleString?.() || myScore} pts. Watch the bracket:`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title: "Cosmic Worm Breaker — Bracket",
          text,
          url: spectatorUrl,
        });
        toast.success("Shared");
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}\n${spectatorUrl}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
        toast.success("Replay link copied", {
          description: "Paste it in Discord / Twitter — the bracket card unfurls automatically.",
        });
      } else {
        toast.success("Replay link", { description: spectatorUrl });
      }
    } catch {
      /* user cancelled share */
    }
  };
  return (
    <div
      className="glass p-6 sm:p-10 border flex flex-col items-center text-center w-full"
      style={{
        borderColor: `${tone}80`,
        boxShadow: `0 0 26px ${tone}33`,
        minHeight: "min(480px, 80vh)",
      }}
      data-testid="tournament-match-complete-card"
      data-outcome={outcome}
    >
      <div
        className="w-16 h-16 flex items-center justify-center border mb-4"
        style={{ borderColor: `${tone}AA`, color: tone }}
      >
        <Icon className="w-7 h-7" />
      </div>
      <div
        className="font-mono-cosmic text-[10px] tracking-[0.35em] uppercase mb-2"
        style={{ color: tone }}
        data-testid="tournament-match-status"
      >
        // BRKT {tournamentId} · LVL {String(level).padStart(2, "0")}
      </div>
      <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter text-white mb-3">
        {theme.label}
      </h2>
      <p className="text-white/60 text-sm max-w-md mb-6">{theme.desc}</p>

      {/* Score breakdown — same grid pattern as the daily LockedCard. */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm mb-6">
        <div
          className="glass px-3 py-2 border"
          style={{ borderColor: `${tone}66` }}
          data-testid="tournament-match-my-score"
        >
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
            Your Score
          </div>
          <div
            className="font-heading font-black text-lg tabular-nums"
            style={{ color: tone }}
          >
            {fmt(myScore)}
          </div>
        </div>
        <div
          className="glass px-3 py-2 border border-white/15"
          data-testid="tournament-match-opp-score"
        >
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
            {oppName || "Opponent"}{oppIsBot ? " · Bot" : ""}
          </div>
          <div className="font-heading font-black text-lg tabular-nums text-white">
            {oppScore == null ? (
              <span className="text-white/40 italic text-sm">awaiting…</span>
            ) : (
              fmt(oppScore)
            )}
          </div>
        </div>
      </div>

      {/* CTAs — primary returns to bracket, secondary heads back to the
          tournament hub so the pilot can quickly hop into another match.
          The Share button copies an OG-rich /api/share/replay/<id> link
          (Discord/Twitter unfurl into a beautiful bracket card via the
          server-side OG meta tags). */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to={`/tournaments/${tournamentId}`}
            className="btn-neon flex-1 inline-flex items-center justify-center gap-2"
            style={{ borderColor: tone, color: tone }}
            data-testid="tournament-match-view-bracket-btn"
          >
            <Swords className="w-4 h-4" /> View Bracket
          </Link>
          <Link
            to="/tournaments"
            className="btn-neon cyan flex-1 inline-flex items-center justify-center gap-2"
            data-testid="tournament-match-back-to-hub-btn"
          >
            Hub <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <button
          type="button"
          onClick={handleShare}
          className="btn-neon inline-flex items-center justify-center gap-2 border-white/30 text-white/85 hover:border-white/60"
          data-testid="tournament-match-share-btn"
        >
          {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          {copied ? "Copied!" : "Share Match"}
        </button>
      </div>
    </div>
  );
}
